<template>
  <!-- The not-found page (sent with 404, utils/pageStatus.js) and the error page, drawn in the site's own theme:
       the theme's display font and button, else the business's brand colours. -->
  <section class="page-missing" :aria-labelledby="headingId">
    <p class="page-missing__code" aria-hidden="true">{{ isError ? "Oops" : "404" }}</p>
    <h1 :id="headingId" class="page-missing__title">{{ isError ? "Something went wrong on our side" : "Sorry, we can't find that page" }}</h1>
    <p class="page-missing__text">
      {{ isError
        ? "Please refresh the page, or try again in a minute."
        : "It may have moved, or the link may be out of date. Try our home page, or get in touch and we'll help you find it." }}
    </p>
    <div class="page-missing__actions">
      <router-link to="/" class="t-btn page-missing__btn">Go to the home page</router-link>
      <router-link v-if="!isError && hasContact" to="/contact" class="t-btn t-btn--ghost page-missing__btn page-missing__btn--ghost">Contact us</router-link>
    </div>
  </section>
</template>

<script setup>
import {computed} from "vue";
import {usePageStore} from "@/stores/pageStore";

const props = defineProps({
  isError: {
    type: Boolean,
    default: false
  }
});

const headingId = "page-missing-title";
const pageStore = usePageStore();
// The contact page is part of every site; only offered when the site says it's there.
const hasContact = computed(() => pageStore.pageData?.availablePages?.contact !== false);
</script>

<style scoped>
.page-missing {
  max-width: 40rem;
  margin: 0 auto;
  padding: 4rem 1.25rem;
  text-align: center;
  color: var(--t-ink, var(--brand-text-primary, inherit));
}
.page-missing__code {
  margin: 0 0 .5rem;
  font-family: var(--t-font-display, var(--theme-font-display, inherit));
  font-weight: var(--t-display-weight, 800);
  font-size: clamp(3.5rem, 12vw, 6rem);
  line-height: 1;
  color: var(--t-accent, var(--brand-accent, currentColor));
}
.page-missing__title {
  margin: 0 0 1rem;
  font-family: var(--t-font-display, var(--theme-font-display, inherit));
  font-weight: var(--t-display-weight, 700);
  font-size: clamp(1.6rem, 4vw, 2.25rem);
  line-height: 1.2;
}
.page-missing__text {
  margin: 0 auto 2rem;
  max-width: 32rem;
  font-size: 1.05rem;
  line-height: 1.6;
  opacity: .85;
}
.page-missing__actions {
  display: flex;
  flex-wrap: wrap;
  gap: .75rem;
  justify-content: center;
}
/* Themes without their own button tokens (the classic ones) get the brand colours. */
.page-missing__btn {
  background: var(--t-btn-bg, var(--brand-accent, #17375d));
  color: var(--t-btn-ink, var(--brand-white, #ffffff));
  border-radius: var(--t-btn-radius, 999px);
  min-height: 44px;
}
.page-missing__btn--ghost {
  background: transparent;
  color: var(--t-ink, var(--brand-text-primary, currentColor));
}
</style>
